import { useQuery } from "react-query";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { SetStateAction, useContext, useEffect, useState } from "react";
import "./results.scss";
import Loader from "../components/loader";
import LoadError from "../components/error";
import { ApiEndpoint } from "../components/utils";
import { AppDataContext, ctxDefinition } from "../components/ctx";
import { FormControlLabel, MenuItem, Select, Switch } from "@mui/material";
import "react-circular-progressbar/dist/styles.css";
import { BrandButton } from "../components/form/buttons";
import NetworkError from "../components/networkError";

interface UserStats {
  headline?: string;
  sat_summary?: any;
  act_summary?: any;
  act_answers?: any;
  sat_answers?: any;
  summary_tables?: any;
  decision?: string;
  userid: string;
  firstName: string;
  lastName: string;
  currentSectionId: number;
  lastQuestionAnswered: number;
  sectionStartTime: number;
  examComplete: boolean;
  satMathLevel: number;
  satVerbalLevel: number;
}

interface SectionData {
  correctAnswers: number;
  incorrectAnswers: number;
  nonAnswered: number;
  score: number;
}

const Admin = () => {
  let { userData } = useContext(AppDataContext) as ctxDefinition;
  const [queryParameters] = useSearchParams();
  const userid = queryParameters.get("id")
    ? parseInt(queryParameters.get("id") as string)
    : userData.userid;
  const token = queryParameters.get("token");

  if (!userData && token) {
    userData = {} as ctxDefinition;
    userData.token = token;
    userData.userid = userid;
    userData.isadmin = true;
  }
  const [data, setdata] = useState<UserStats | null>();
  const targetDataUser = userData.userid;
  const [sectionId, setSectionId] = useState();
  const [sectionData, setSectionData] = useState<SectionData>();
  const [time, setTime] = useState();
  const [hasError, setHasError] = useState(false);
  const [loadingSectionData, setLoadingSectionData] = useState(false);
  const [satVerbalLevel, setSatVerbalLevel] = useState(0);
  const [satMathLevel, setSatMathLevel] = useState(0);
  const [timingOption, setTimingOption] = useState(0);

  const timingOptions = [
    {
      id: "0.0",
      name: "Standard",
    },
    {
      id: 1,
      name: "Extended",
    },
  ];

  const sectionOptions = [
    {
      id: 0,
      name: "ACT English",
    },
    {
      id: 1,
      name: "Reading and Writing Module 1",
    },
    {
      id: 2,
      name: "ACT Math",
    },
    {
      id: 3,
      name: "SAT Reading and Writing Module 2",
    },
    {
      id: 4,
      name: "SAT Reading and Writing Module 2b",
    },
    {
      id: 5,
      name: "ACT Reading",
    },
    {
      id: 6,
      name: "SAT Math Module 1",
    },
    {
      id: 7,
      name: "ACT Science",
    },
    {
      id: 8,
      name: "SAT Math Module 2",
    },
    {
      id: 9,
      name: "SAT Math Module 2b",
    },
  ];

  const { error, refetch } = useQuery(
    "examResultsPageData",
    async () => {
      const resp = await axios.get(
        ApiEndpoint +
          `wp-json/firstchoiceprep/v1/getUserData?token=${userData.token}&userID=${targetDataUser}`
      );
      setSectionId(resp.data.currentSectionId);
      setTime(resp.data.sectionStartTime);

      setSatVerbalLevel(resp.data.satVerbalLevel);
      setSatMathLevel(resp.data.satMathLevel);
      setTimingOption(
        resp.data.examType === -1 || resp.data.examType === 0
          ? "0.0"
          : resp.data.examType
      );
      setdata(resp.data);
      if (!resp || !resp.data.success) {
        setHasError(true);
        return;
      }
    },
    {
      refetchOnWindowFocus: false,
    }
  );

  const udpateData = async () => {
    await axios.post(
      ApiEndpoint + "wp-json/firstchoiceprep/v1/updateUserData",
      {
        token: userData ? userData.token : token,
        userid: targetDataUser,
        userID: targetDataUser,
        fields: [
          "currentSectionId",
          "lastQuestionAnswered",
          "sectionStartTime",
          "satMathLevel",
          "satVerbalLevel",
          "examType",
        ],
        values: [
          sectionId,
          null,
          time,
          satMathLevel,
          satVerbalLevel,
          timingOption,
        ],
      }
    );
    setSectionId("" as any);
    setTime("" as any);
  };

  const getSectionIdData = async () => {
    if (!sectionId && sectionId !== 0) return;
    setLoadingSectionData(true);
    const resp = await axios.post(
      ApiEndpoint + "wp-json/firstchoiceprep/v1/calculateSectionScore",
      {
        token: userData.token,
        sectionId: sectionId,
        userid: userData.userid,
      }
    );
    setSectionData(resp.data);
    setLoadingSectionData(false);
  };

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetDataUser]);

  useEffect(() => {
    getSectionIdData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionId]);

  const stampToTime = (): string => {
    return new Date().toLocaleString();
  };

  if (!userData && !token) return <LoadError />;
  if (error || (userData && !userData.isadmin)) return <LoadError />;
  if (!data) return <Loader />;

  return (
    <div className="resultsPage">
      <div className="headline">
        <h1>User Admin Dashboard</h1>
      </div>
      <div className="pageContent results">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            margin: "30px",
          }}
        >
          <div className="" style={{ display: "flex", gap: "35px" }}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <strong>Current Section ID:</strong>
              <br />{" "}
              <Select
                labelId="demo-simple-select-label"
                id="section-select"
                value={sectionId}
                label="Current Section"
                onChange={(evt: any) => setSectionId(evt.target.value)}
              >
                {sectionOptions.map((item) => {
                  return (
                    <MenuItem value={item.id}>
                      {item.id} - {item.name}
                    </MenuItem>
                  );
                })}
              </Select>
              <div>
                <br />
                <strong>Current Section Stats:</strong>
                {loadingSectionData ? (
                  <Loader />
                ) : (
                  <ul>
                    <li>
                      <strong>Correct:</strong>{" "}
                      {sectionData ? sectionData.correctAnswers : ""}
                    </li>
                    <li>
                      <strong>Incorrect:</strong>{" "}
                      {sectionData ? sectionData.incorrectAnswers : ""}
                    </li>
                    <li>
                      <strong>Non-answered:</strong>{" "}
                      {sectionData ? sectionData.nonAnswered : ""}
                    </li>
                    <li>
                      <strong>Score:</strong>{" "}
                      {sectionData ? sectionData.score + "%" : ""}
                    </li>
                  </ul>
                )}
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <strong>Timing Options:</strong>
              <br />{" "}
              <Select
                labelId="demo-simple-select-label"
                id="section-select"
                value={timingOption}
                label="Current Section"
                onChange={(evt: any) => setTimingOption(evt.target.value)}
              >
                {timingOptions.map((item) => {
                  return <MenuItem value={item.id}>{item.name}</MenuItem>;
                })}
              </Select>
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <strong>Time Update:</strong>
              <br />{" "}
              <BrandButton
                onClick={() =>
                  setTime(
                    new Date().getTime() as unknown as SetStateAction<undefined>
                  )
                }
              >
                Set Time to Now
              </BrandButton>
              <p>{stampToTime()}</p>
            </div>
            <div>
              <div>
                <strong>Difficulty Level:</strong>
                <p>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={satVerbalLevel === 0 ? false : true}
                        onChange={(evt: any) =>
                          evt.target.checked
                            ? setSatVerbalLevel(1)
                            : setSatVerbalLevel(0)
                        }
                      />
                    }
                    label="SAT Verbal 2b "
                  />
                </p>
              </div>
              <div>
                <FormControlLabel
                  control={
                    <Switch
                      checked={satMathLevel === 0 ? false : true}
                      onChange={(evt: any) =>
                        evt.target.checked
                          ? setSatMathLevel(1)
                          : setSatMathLevel(0)
                      }
                    />
                  }
                  label="SAT Math 2b"
                />
              </div>
            </div>
          </div>
        </div>
        <hr style={{ marginTop: "0" }} />
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginRight: "30px",
          }}
        >
          <BrandButton onClick={() => udpateData()}>Update Data</BrandButton>
        </div>
      </div>
      <NetworkError open={hasError} />
    </div>
  );
};
export default Admin;
